// Header layout helpers for the monthly report's multi-level column headers.
// A ColumnNode is { key, label, children?: ColumnNode[] }. Leaves have no children.

// Depth of a single node, leaf = 1.
function nodeDepth(node) {
  if (!node.children || node.children.length === 0) return 1;
  return 1 + Math.max(...node.children.map(nodeDepth));
}

// Deepest node depth across an entire columnTree (a client's own tree).
export function getTreeDepth(columnTree) {
  const nodes = columnTree || [];
  if (nodes.length === 0) return 1;
  return Math.max(...nodes.map(nodeDepth));
}

// Number of leaf descendants under a node (1 if it's already a leaf).
export function countLeaves(node) {
  if (!node.children || node.children.length === 0) return 1;
  return node.children.reduce((sum, child) => sum + countLeaves(child), 0);
}

// Splits a client's columnTree into `globalMaxDepth` header rows. Each row is
// an array of { key, label, colSpan, rowSpan } cells, left-to-right.
// A leaf's rowSpan stretches it down to the last row so shallower clients
// (e.g. a flat "simple" layout) still line up under deeper ones (e.g. a
// 3-level "advanced" layout) sharing the same <thead>.
export function buildHeaderRows(columnTree, globalMaxDepth) {
  const rows = Array.from({ length: globalMaxDepth }, () => []);

  const walk = (nodes, depth) => {
    (nodes || []).forEach((node) => {
      const isLeaf = !node.children || node.children.length === 0;
      rows[depth - 1].push({
        key: node.key,
        label: node.label,
        colSpan: countLeaves(node),
        rowSpan: isLeaf ? globalMaxDepth - depth + 1 : 1,
      });
      if (!isLeaf) walk(node.children, depth + 1);
    });
  };

  walk(columnTree, 1);
  return rows;
}

// Flat list of header cells positioned in a single client's own column
// block: { row (0-based within the header block), colStart (0-based within
// this client's leafColumns), colSpan, rowSpan, label }. Used by the Excel
// export to build real merged-cell ranges instead of one label per column.
export function collectHeaderCells(columnTree, globalMaxDepth) {
  const cells = [];
  const cursor = { value: 0 };

  const walk = (nodes, depth) => {
    (nodes || []).forEach((node) => {
      const isLeaf = !node.children || node.children.length === 0;
      const colStart = cursor.value;
      const colSpan = countLeaves(node);
      cells.push({
        row: depth - 1,
        colStart,
        colSpan,
        rowSpan: isLeaf ? globalMaxDepth - depth + 1 : 1,
        label: node.label,
      });
      if (isLeaf) {
        cursor.value += 1;
      } else {
        walk(node.children, depth + 1);
      }
    });
  };

  walk(columnTree, 1);
  return cells;
}
